/* Chat assistant page (sidebar, right under the Dashboard; CONTEXT.md). One Account at a
   time, showing exactly what that Account's Chat Assistant tab shows: the Account header
   without the tab row, then the Chat Assistant sub-tabs. The header's avatar and name are
   the Account switcher. The choice lives in ?account= (the sub-tab in ?sub=, kept across
   switches) and the last one used is the one the Mass messages pages remember too. */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Account, type Model } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useAccounts } from '../context/AccountsContext';
import { useToolAccess } from '../hooks/useToolAccess';
import { hasChatAssistantAccess, hasTokenAccess, isAccountOwner } from '../permissions';
import { AccountHeader } from '../components/AccountHeader';
import { ChatAssistantTab } from '../components/ChatAssistant/ChatAssistantTab';
import { LoadingBlock } from '../components/Spinner';
import { PageTransition } from '../components/PageTransition';
import { accountName } from '../dashboard/attention';
import { chooseAccount, offeredAccounts, readLastAccount, rememberAccount } from './pickAccount';

export function ChatAssistantToolPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const tools = useToolAccess();
  const { accounts, loaded, subscriptionMap, updateAccount } = useAccounts();
  const { getPermissions, loadAccountPermissions } = useAuth();
  const [params, setParams] = useSearchParams();

  const offered = useMemo(() => {
    if (!tools || !loaded) return null;
    return offeredAccounts(accounts, tools.mass_pages?.chat_assistant)
      .slice()
      .sort((a, b) => accountName(a).localeCompare(accountName(b)));
  }, [tools, loaded, accounts]);
  const pickerAccounts = useMemo(
    () => (offered ?? []).map((a) => ({ account_id: a.account_id, name: accountName(a), username: a.username, platform: a.platform })),
    [offered],
  );

  const fromUrl = params.get('account');
  const accountId = offered ? chooseAccount(offered.map((a) => a.account_id), fromUrl, readLastAccount()) : undefined;
  const listed = offered?.find((a) => a.account_id === accountId);

  // Keep the URL on the Account shown (a bare open, or one no longer offered).
  useEffect(() => {
    if (!accountId || accountId === fromUrl) return;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('account', accountId);
      return next;
    }, { replace: true });
  }, [accountId, fromUrl, setParams]);

  // As the Account page does: the Account fresh, its permissions (the sub-tabs read them), its model.
  const [loadedAccount, setLoadedAccount] = useState<Account | null>(null);
  const [model, setModel] = useState<Model | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    if (!accountId) return;
    rememberAccount(accountId);
    let live = true;
    setFailed(null);
    setModel(null);
    Promise.all([api.getAccount(accountId), loadAccountPermissions(accountId)]).then(
      ([fresh]) => {
        if (!live) return;
        setLoadedAccount(fresh);
        updateAccount(fresh.account_id, fresh);
        if (fresh.model_id) api.getModel(fresh.model_id).then((m) => { if (live) setModel(m); }, () => {});
      },
      () => { if (live) setFailed(accountId); },
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadAccountPermissions / updateAccount are recreated every render
  }, [accountId]);

  // Never show the previous Account's data while the next one loads.
  const account = loadedAccount?.account_id === accountId ? loadedAccount : null;
  const perms = account ? getPermissions(account.account_id) : undefined;

  const choose = (id: string) => {
    if (id === accountId) return;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('account', id);
      return next;
    });
  };

  const onAccountUpdate = (updated: Account) => {
    setLoadedAccount(updated);
    updateAccount(updated.account_id, updated);
  };

  if (!offered) return <div className="mobile-page-shell p-6 min-w-0"><LoadingBlock /></div>;
  if (!accountId || !listed) {
    return (
      <div className="mobile-page-shell p-6 min-w-0">
        <p style={{ margin: '24px 0', color: 'var(--om-muted)' }}>{t('massPages.chat.none')}</p>
      </div>
    );
  }

  const header = account ?? listed;
  const owner = isAccountOwner(perms);
  const body = () => {
    if (failed === accountId) return <p style={{ margin: '24px 0', color: 'var(--om-muted)' }}>{t('massPages.permsFailed')}</p>;
    if (!account || !perms) return <LoadingBlock />;
    // Keyed by Account: a pending autosave flushes to the Account it was typed on.
    return <ChatAssistantTab key={account.account_id} account={account} onAccountUpdate={onAccountUpdate} />;
  };

  return (
    <PageTransition>
      <div className="mobile-page-shell p-6 min-w-0">
        <AccountHeader
          account={header}
          modelName={model?.model_name || null}
          subStatus={subscriptionMap[accountId] || null}
          switcher={{ accounts: pickerAccounts, onChange: choose }}
          onSettings={perms && (owner || hasTokenAccess(perms)) ? () => navigate(`/account/${accountId}/settings`) : undefined}
          onAccess={perms && owner ? () => navigate(`/account/${accountId}?tab=access`) : undefined}
          onFinishSetup={account && hasChatAssistantAccess(perms) && !account.ai_setup_completed_at ? () => navigate(`/account/${accountId}/ai-setup`) : undefined}
        />
        {body()}
      </div>
    </PageTransition>
  );
}
